import { defineComponent as _defineComponent } from 'vue'
import { ref } from 'vue';
import { useI18n } from '@/composables';


import { getMetaInfo } from '@/utils/seo';

const __default__ = {
  name: 'SitemapPage',
  metaInfo() {
    return getMetaInfo({ title: (this as any).$t('sitemapPage.title') });
  },
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { t } = useI18n();
const loading = ref(true);
const error = ref<string | null>(null);
const groups = ref<Array<{ label: string; entries: Array<{ url: string; label: string }> }>>([]);

/**
 * Sitemap XML from CMS may list another host; links should stay on the app the user is on.
 */
function toCurrentOriginHref(remoteUrl: string) {
  const { origin } = globalThis.location;
  try {
    const parsed = new URL(remoteUrl, origin);
    return `${origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return remoteUrl;
  }
}

function getStrapiSitemapUrl() {
  // eslint-disable-next-line no-undef
  const clusterHost = config.VUE_APP_CLUSTER_HOST || process.env.VUE_APP_CLUSTER_HOST;
  // eslint-disable-next-line no-undef
  const strapiUrl = config.VUE_APP_STRAPI_URL || process.env.VUE_APP_STRAPI_URL || '';
  // eslint-disable-next-line no-undef
  const strapiClusterUrl = config.VUE_APP_STRAPI_CLUSTER_URL || process.env.VUE_APP_STRAPI_CLUSTER_URL || strapiUrl;

  const { hostname } = new URL(globalThis.location.href);
  const baseUrl = hostname === clusterHost ? strapiClusterUrl : strapiUrl;

  return `${baseUrl}/api/sitemap/index.xml`;
}

function formatLabel(segment: string) {
  return segment
    .replaceAll('-', ' ')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

function groupUrls(urls: string[]) {
  const map = new Map<string, Array<{ url: string; label: string }>>();

  urls.forEach((url) => {
    const href = toCurrentOriginHref(url);
    let pathname;
    try {
      pathname = new URL(href).pathname;
    } catch {
      pathname = url;
    }

    const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);
    const topSegment = segments[0] || '';

    const homeLabel = String(t('sitemapPage.home'));
    const groupLabel = formatLabel(topSegment) || homeLabel;
    const entryLabel = segments.length > 1
      ? segments.slice(1).map(formatLabel).join(' › ')
      : formatLabel(topSegment) || homeLabel;

    if (!map.has(groupLabel)) {
      map.set(groupLabel, []);
    }
    map.get(groupLabel)!.push({ url: href, label: entryLabel });
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, entries]) => ({ label, entries }));
}

function parseSitemap(xmlText: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'application/xml');
  const ns = 'http://www.sitemaps.org/schemas/sitemap/0.9';

  const urlNodes = doc.getElementsByTagNameNS(ns, 'url');
  const locNodes = doc.getElementsByTagNameNS(ns, 'loc');

  // Handle sitemap index (collection of sub-sitemaps) vs regular sitemap
  const isIndex = !urlNodes.length && locNodes.length > 0;

  const rawUrls = isIndex
    ? Array.from(locNodes).map((loc) => (loc.textContent || '').trim())
    : Array.from(urlNodes)
      .map((url) => {
        const loc = url.getElementsByTagNameNS(ns, 'loc')[0];
        return loc ? (loc.textContent || '').trim() : null;
      })
      .filter(Boolean) as string[];

  return groupUrls(rawUrls);
}

async function fetchSitemapText() {
  // Primary: nginx-cached copy served same-origin (avoids CORS entirely)
  try {
    const response = await fetch('/sitemap.xml');
    if (response.ok) {
      return response.text();
    }
  } catch {
    // not available locally, fall through to direct Strapi fetch
  }

  // Fallback: fetch directly from Strapi (requires CORS to be open on the CMS)
  const strapiUrl = getStrapiSitemapUrl();
  const response = await fetch(strapiUrl);

  if (!response.ok) {
    throw new Error(`Failed to load sitemap (HTTP ${response.status})`);
  }

  return response.text();
}

async function fetchSitemap() {
  loading.value = true;
  error.value = null;

  try {
    const text = await fetchSitemapText();
    groups.value = parseSitemap(text);
  } catch (e) {
    error.value = String(t('sitemapPage.errorLoadFailed'));
    console.error('[SitemapPage]', e);
  } finally {
    loading.value = false;
  }
}

fetchSitemap();

return { __sfc: true,t, loading, error, groups, toCurrentOriginHref, getStrapiSitemapUrl, formatLabel, groupUrls, parseSitemap, fetchSitemapText, fetchSitemap }
}

})